// app/(app)/layout.tsx
"use client";

import { Suspense, useEffect, useSyncExternalStore } from 'react';
import AppInputLayer from '@/components/AppInputLayer';
import { KernelProvider } from '@/kernel/Kernel';
import { QuickMenu } from '@/components/QuickMenu';
import { useKernelStore } from '@/kernel/kernelStores';
import AppUiLayer from '@/components/AppUiLayer';
import { DaimonProvider } from '@/components/daimon/DaimonVoice';
import UrwPaymentNotice from '@/components/UrwPaymentNotice';
import { AnalyticsProvider } from '@/kernel/useAnalytics';
import { StoreProvider } from '@/components/StoreProvider';
import { usePathname, useRouter } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import { safeRedirectPath } from '#lib/safeRedirectPath';
import SimpleLoader from '@/components/ui/SimpleLoader';

const subscribeToHydration = () => () => {};
const clientHydrated = () => true;
const serverHydrated = () => false;

const QuickMenuLayer = () => {
  const menuData = useKernelStore((state) => state.menuData);
  return <QuickMenu menuData={menuData} />;
};

// Inner component to consume the store context
const AppLayoutContent = ({ children }: { children: React.ReactNode }) => {
  return (
    <KernelProvider>
      <AppInputLayer>
        <DaimonProvider>{children}</DaimonProvider>
        <QuickMenuLayer />
        <AppUiLayer />
        <UrwPaymentNotice />
      </AppInputLayer>
    </KernelProvider>
  );
};

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const hydrated = useSyncExternalStore(subscribeToHydration, clientHydrated, serverHydrated);
  const { isLoaded, isSignedIn } = useUser();
  const pathname = usePathname() || '';
  const router = useRouter();
  const isDevPreview = process.env.NODE_ENV !== 'production' && pathname.startsWith('/dev-preview/');

  useEffect(() => {
    if (!isLoaded || isSignedIn || isDevPreview) return;
    const returnPath = safeRedirectPath(window.location.pathname + window.location.search + window.location.hash);
    router.replace(`/sign-in?redirect_url=${encodeURIComponent(returnPath)}`);
  }, [isLoaded, isSignedIn, isDevPreview, pathname, router]);

  // Account effects belong after authentication, not behind the dashboard's loading screen.
  if (!hydrated || (!isDevPreview && (!isLoaded || !isSignedIn))) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-black text-white">
        <SimpleLoader />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full bg-black overflow-hidden">
      <AnalyticsProvider>
        <StoreProvider>
          <Suspense fallback={null}>
            <AppLayoutContent>{children}</AppLayoutContent>
          </Suspense>
        </StoreProvider>
      </AnalyticsProvider>
    </div>
  );
}
